import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Box, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { BodyText } from '@/components/BodyText';
import { FailureKind } from '@/hooks/api/assets/useGetRecentFailures';
import { FailureKindList } from './FailureKindList';
import { FAILURE_TABLE_GRID_COLUMN, failureTableRowLayout } from './failureTableLayout';
import { BatchRemediationButton } from './remediation/BatchRemediationButton';

export interface FailureKindSummary {
  kind: FailureKind;
  type: string;
  workflow: string;
  count: number;
  mostRecent?: string;
}

const HeaderRow = styled(Box)(({ theme }) => ({
  ...failureTableRowLayout,
  paddingTop: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTypography-root': {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
}));

const StaticRow = styled(Box)(failureTableRowLayout);

const SummaryRow = styled(Box)(({ theme }) => ({
  ...failureTableRowLayout,
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
  },
}));

const ExpandToggle = styled('button')(({ theme }) => ({
  gridColumn: FAILURE_TABLE_GRID_COLUMN.beforeAction,
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  alignItems: 'center',
  padding: 0,
  border: 'none',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  color: theme.palette.text.primary,
  backgroundColor: 'transparent',
}));

const ExpandedItems = styled(Box)({
  paddingBottom: '8px',
});

const CountText = styled(BodyText)({
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
});

const formatMostRecent = (mostRecent?: string) =>
  mostRecent ? formatDistanceToNow(new Date(mostRecent), { addSuffix: true }) : '-';

const SummaryCells = ({ summary }: { summary: FailureKindSummary }) => (
  <>
    <BodyText>{summary.type}</BodyText>
    <BodyText color='text.secondary'>{summary.workflow}</BodyText>
    <CountText>{summary.count.toLocaleString()}</CountText>
    <BodyText color='text.secondary'>{formatMostRecent(summary.mostRecent)}</BodyText>
  </>
);

const FailureSummaryRow = ({ summary }: { summary: FailureKindSummary }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const testId = `failure-summary-${summary.kind}`;

  if (summary.count === 0) {
    return (
      <StaticRow data-testid={testId}>
        <span />
        <SummaryCells summary={summary} />
      </StaticRow>
    );
  }

  return (
    <>
      <SummaryRow>
        <ExpandToggle
          type='button'
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(current => !current)}
          data-testid={testId}
        >
          {isExpanded ? <ExpandMore fontSize='small' /> : <ChevronRight fontSize='small' />}
          <SummaryCells summary={summary} />
        </ExpandToggle>
        <BatchRemediationButton kind={summary.kind} />
      </SummaryRow>
      {isExpanded && (
        <ExpandedItems>
          <FailureKindList kind={summary.kind} />
        </ExpandedItems>
      )}
    </>
  );
};

export const FailureSummaryTable = ({ summaries }: { summaries: FailureKindSummary[] }) => (
  <Box display='flex' flexDirection='column' gap='2px' data-testid='failure-summary-table'>
    <HeaderRow>
      <span />
      <BodyText>Type</BodyText>
      <BodyText>Workflow</BodyText>
      <BodyText textAlign='right'>Count</BodyText>
      <BodyText>Most recent</BodyText>
      <span />
    </HeaderRow>
    {summaries.map(summary => (
      <FailureSummaryRow key={summary.kind} summary={summary} />
    ))}
  </Box>
);
